import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { trackReleaseStrategyBackClicked } from '@gonfalon/automated-rollouts/analytics';
import { enableAlternateScheduledChangeForm } from '@gonfalon/dogfood-flags/dogfood-flags';
import { RequiredLabel } from '@gonfalon/launchpad-experimental/RequiredLabel';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, ButtonGroup } from '@launchpad-ui/components';
import { useStore } from '@tanstack/react-form';
import { clsx } from 'clsx';

import { useAiConfigApprovalSettings } from '#components/AiConfigs/AiConfigApprovals/useAiConfigApprovalSettings';
import {
  type PendingChangesFormType,
  usePendingChangesFormAdapter,
} from '#components/PendingChanges/NewPendingChangesForm/usePendingChangesFormAdapter';
import { timedFlagTargetingEvents, trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';

import { ApprovalIntegrationFields } from '../ApprovalIntegrationFields';
import type { ReviewersMap } from '../FormSection/Approvals/SelectReviewerTag';
import { usePendingChangesContext } from '../PendingChangesContext';
import type { PendingChangesFormProps } from '../pendingChangesFormTypes';

import { CustomSchedule } from './CustomSchedule';
import { useReviewAndSaveContext } from './reviewAndSaveContext';
import { SaveButton } from './SaveButton';
import { ScheduleChangesPopover } from './ScheduleChangesPopover';
import type { FormRequirements, ResourceInfo } from './schema';
import { SelectReviewersDisclosure } from './SelectReviewers';
import { SelectReviewersTrigger } from './SelectReviewersTrigger';
import { getCancelButtonText, getFormRenderingConfig, getSaveButtonText } from './utilts';

import styles from './PendingChangesModal.module.css';

interface NewPendingChangesFormProps extends Omit<
  PendingChangesFormProps,
  'isAIFlag' | 'pendingChangesFormState' | 'resourceValidation'
> {
  pendingChangesForm: PendingChangesFormType;
  enableFilterByApprovers?: boolean;
  canReviewOwnRequest: boolean;
  isUpdatingScheduledChange: boolean;
  resourceInfo: ResourceInfo;
  formRequirements: FormRequirements;
  setReviewersMap: Dispatch<SetStateAction<ReviewersMap>>;
}

export function NewPendingChangesForm({
  hideScheduleOption,
  hideApprovalOption,
  saveOption,
  approvalServiceKind,
  approvalServiceManifest,
  flagKey,
  useBackButton,
  absoluteModalFooterClassName,
  pendingChangesForm,
  enableFilterByApprovers,
  canReviewOwnRequest,
  isUpdatingScheduledChange,
  resourceInfo: { resourceKey, resourceName },
  formRequirements: {
    isAcknowledgeConflictsRequired,
    isConfirmationRequired,
    isApprovalRequired: defaultIsApprovalRequired,
    isCommentRequired,
    usesLaunchDarklyApprovalService,
    minNumberOfApprovals,
    canBypassRequiredApprovals,
  },
  onCancel,
  onBack,
  setReviewersMap,
}: NewPendingChangesFormProps) {
  const {
    isCustomScheduleSelected,
    setIsCustomScheduleSelected,
    testRunEnabled,
    setIsJsonDiffingEnabled,
    setTestRunEnabled,
    isJsonDiffingEnabled,
  } = useReviewAndSaveContext();
  const [showOptionalReviewers, setShowOptionalReviewers] = useState(false);
  const alternateScheduledChangeForm = enableAlternateScheduledChangeForm();
  const filterOutCurrentMember = !canReviewOwnRequest;

  const handleBack = () => {
    trackReleaseStrategyBackClicked();
    onBack?.();
  };

  const { maintainerIds, maintainerTeamKeys, bypassRequiredApprovals } = useStore(
    pendingChangesForm.store,
    (state) => ({
      maintainerIds: state.values.reviewers.filter((m) => m.type === 'member').map((m) => m.id),
      maintainerTeamKeys: state.values.reviewers.filter((m) => m.type === 'team').map((m) => m.id),
      bypassRequiredApprovals: state.values.bypassRequiredApprovals,
    }),
  );

  const reviewerCount = maintainerIds.length + maintainerTeamKeys.length;
  const isApprovalRequired = bypassRequiredApprovals ? false : defaultIsApprovalRequired;

  const {
    showScheduledChangesForm,
    showSecondaryScheduleChangesAction,
    showApprovalIntegration,
    showReviewers,
    showComment,
    showConfirmation,
    showAcknowledgeConflicts,
    showSecondaryReviewersPopover,
    showReviewerInline,
    showReviewerInFooter,
    showBypassRequiredApprovals,
  } = getFormRenderingConfig({
    testRunEnabled,
    isCustomScheduleSelected,
    isUpdatingScheduledChange,
    reviewerCount,
    isApprovalRequired,
    isConfirmationRequired: isConfirmationRequired ?? false,
    hideApprovalOption: hideApprovalOption ?? false,
    hideScheduleOption: hideScheduleOption ?? false,
    usesLaunchDarklyApprovalService,
    saveOption,
    isAcknowledgeConflictsRequired,
    bypassRequiredApprovals,
    showOptionalReviewers,
    canBypassRequiredApprovals,
  });

  const isApprovalRequestedOrRequired = isApprovalRequired || reviewerCount > 0;
  const commentIsRequired = bypassRequiredApprovals
    ? isCommentRequired
    : isCommentRequired || isApprovalRequestedOrRequired;
  const cancelButtonText = getCancelButtonText({
    testRunEnabled,
    isJsonDiffingEnabled,
    isCustomScheduleSelected,
    useBackButton,
  });

  const saveButtonText = getSaveButtonText({
    isUpdatingScheduledChange,
    isCustomScheduleSelected,
    isApprovalRequestedOrRequired,
    saveOption,
    bypassRequiredApprovals,
  });

  const handleCancel = () => {
    if (testRunEnabled) {
      setTestRunEnabled(false);
    } else if (isJsonDiffingEnabled) {
      setIsJsonDiffingEnabled(false);
    } else if (isCustomScheduleSelected) {
      pendingChangesForm.setFieldValue('executionDate', 0);
      pendingChangesForm.setFieldValue('isScheduleSelected', false);
      void pendingChangesForm.validate('change');
      setIsCustomScheduleSelected(false);
    } else if (useBackButton) {
      handleBack();
    } else {
      trackFlagTargetingEvent.measureAndTrack(timedFlagTargetingEvents.TIME_ON_REVIEW_AND_SAVE, {
        type: 'cancel',
        saveAction: saveButtonText,
      });
      onCancel();
    }
  };

  return (
    <div className={styles.pendingChangesForm} id="search-pending-changes">
      <pendingChangesForm.AppForm>
        {showScheduledChangesForm && <CustomSchedule form={pendingChangesForm} />}

        {showReviewers && (
          <SelectReviewersDisclosure
            form={pendingChangesForm}
            minNumberOfApprovals={minNumberOfApprovals}
            approvalServiceKind={approvalServiceKind}
            isRequired={isApprovalRequired}
            enableFilterByApprovers={enableFilterByApprovers}
            setReviewersMap={setReviewersMap}
            filterOutCurrentMember={filterOutCurrentMember}
            resourceKey={resourceKey}
            maintainerIds={maintainerIds}
            maintainerTeamKeys={maintainerTeamKeys}
            canBypassRequiredApprovals={canBypassRequiredApprovals}
            showOptionalReviewers={showOptionalReviewers}
            showReviewerInline={showReviewerInline}
            showReviewerInFooter={showReviewerInFooter}
            setShowOptionalReviewers={setShowOptionalReviewers}
          />
        )}

        {showApprovalIntegration && approvalServiceManifest && approvalServiceKind && (
          <div className={styles.approvalIntegrationFields}>
            <ApprovalIntegrationFields
              manifest={approvalServiceManifest}
              approvalServiceKind={approvalServiceKind}
              onChange={(integrationConfig) => {
                pendingChangesForm.setFieldValue('integrationConfig', integrationConfig);
              }}
            />
          </div>
        )}

        {showComment && (
          <pendingChangesForm.AppField name="comment">
            {(field) => (
              <field.TextAreaInputField
                name="comment"
                className={styles.formField}
                isRequired={commentIsRequired}
                label="Comment"
                description="Add a brief description of your changes."
              />
            )}
          </pendingChangesForm.AppField>
        )}

        {showConfirmation && (
          <pendingChangesForm.AppField name="resourceConfirmation">
            {(field) => {
              const { isTouched, isDirty, errors } = field.getMeta();

              return (
                <div className={styles.resourceConfirmation}>
                  <field.TextInputField
                    placeholder={resourceKey || resourceName}
                    className={clsx(styles.formField, {
                      [styles.error]: isTouched && isDirty && errors.length > 0,
                    })}
                    name="resourceConfirmation"
                    label="Confirm"
                    description={
                      <>
                        Type <span className={styles.resourceName}>{resourceKey || resourceName}</span> to confirm.
                      </>
                    }
                    isRequired
                  />
                </div>
              );
            }}
          </pendingChangesForm.AppField>
        )}

        {showAcknowledgeConflicts && (
          <pendingChangesForm.AppField name="acknowledgeConflicts">
            {(field) => {
              const { isTouched, isDirty, errors } = field.getMeta();

              return (
                <field.Checkbox
                  className={clsx(styles.formField, {
                    [styles.error]: isTouched && isDirty && errors.length > 0,
                  })}
                  isRequired
                >
                  <RequiredLabel className={styles.checkboxLabel} isRequired>
                    I acknowledge saving this change will introduce conflicts with pending changes
                  </RequiredLabel>
                </field.Checkbox>
              );
            }}
          </pendingChangesForm.AppField>
        )}

        <div slot="footer" className={clsx(absoluteModalFooterClassName, styles.pendingChangesFormFooter)}>
          <div
            className={clsx(styles.footerActions, {
              [styles.multipleActions]: showSecondaryScheduleChangesAction || showSecondaryReviewersPopover,
            })}
          >
            {(showSecondaryScheduleChangesAction || showSecondaryReviewersPopover) && (
              <ButtonGroup>
                {showSecondaryScheduleChangesAction && (
                  <ScheduleChangesPopover
                    setIsCustomScheduleSelected={setIsCustomScheduleSelected}
                    form={pendingChangesForm}
                  />
                )}
                {showSecondaryReviewersPopover && (
                  <SelectReviewersTrigger
                    setShowOptionalReviewers={(showing) => {
                      setShowOptionalReviewers(showing);
                      pendingChangesForm.setFieldValue('bypassRequiredApprovals', false);
                      void pendingChangesForm.validate('change');
                    }}
                    showOptionalReviewers={showOptionalReviewers}
                  />
                )}
              </ButtonGroup>
            )}
            <ButtonGroup>
              <Button onPress={handleCancel} variant="default">
                {cancelButtonText}
              </Button>

              {!testRunEnabled && (
                <SaveButton
                  hideScheduleOption={hideScheduleOption || alternateScheduledChangeForm}
                  saveOption={saveOption}
                  flagKey={flagKey}
                  form={pendingChangesForm}
                  isCustomScheduleSelected={isCustomScheduleSelected}
                  setIsCustomScheduleSelected={setIsCustomScheduleSelected}
                  showBypassRequiredApprovals={showBypassRequiredApprovals}
                  setShowOptionalReviewers={setShowOptionalReviewers}
                >
                  {saveButtonText}
                </SaveButton>
              )}
            </ButtonGroup>
          </div>
        </div>
      </pendingChangesForm.AppForm>
    </div>
  );
}

export function NewPendingChangesFormContainer(props: PendingChangesFormProps) {
  const { isAIFlag, pendingChangesFormState, onEditPendingChangesForm, onSubmit } = props;
  const { environment, enableFilterByApprovers, scheduleAccess, canBypassRequiredApprovals } =
    usePendingChangesContext();
  const projectKey = useProjectKey();
  const [reviewersMap, setReviewersMap] = useState<ReviewersMap>(new Map());

  // Legacy code (approval settings used to only be based on environment)
  const envApprovalSettings = environment.get('approvalSettings');

  // AI config (approval settings can now be different for AI configs)
  const aiConfigApprovalSettings = useAiConfigApprovalSettings({
    projectKey,
    environmentKey: environment.key,
  });
  let canReviewOwnRequest = false;
  let minNumberOfApprovals = 0;

  if (isAIFlag) {
    canReviewOwnRequest = aiConfigApprovalSettings?.currentEnvironment.canReviewOwnRequest ?? false;
    minNumberOfApprovals = aiConfigApprovalSettings?.currentEnvironment.minNumApprovals ?? 0;
  } else {
    canReviewOwnRequest = envApprovalSettings?.get('canReviewOwnRequest') ?? false;
    minNumberOfApprovals = envApprovalSettings?.get('minNumApprovals') ?? 0;
  }

  const {
    form: pendingChangesForm,
    formRequirements,
    resourceInfo,
    isLoading,
    isUpdatingScheduledChange,
  } = usePendingChangesFormAdapter({
    canBypassRequiredApprovals,
    reviewersMap,
    pendingChangesFormState,
    minNumberOfApprovals,
    saveOption: props.saveOption,
    onEditPendingChangesForm,
    onSubmit,
  });

  return isLoading ? null : (
    <NewPendingChangesForm
      {...props}
      hideScheduleOption={props.hideScheduleOption || !scheduleAccess.isAllowed || isAIFlag}
      isUpdatingScheduledChange={isUpdatingScheduledChange}
      setReviewersMap={setReviewersMap}
      canReviewOwnRequest={canReviewOwnRequest}
      pendingChangesForm={pendingChangesForm}
      formRequirements={formRequirements}
      enableFilterByApprovers={enableFilterByApprovers}
      resourceInfo={resourceInfo}
    />
  );
}
